/**
 * 甘特图工具函数
 *
 * 提供日期计算、位置计算、数据转换等工具函数
 */

import {
  differenceInDays,
  addDays,
  startOfDay,
  startOfWeek,
  startOfMonth,
  endOfWeek,
  endOfMonth,
  format,
  parseISO,
  isAfter,
  isBefore,
  isValid,
} from 'date-fns'
import { zhCN } from 'date-fns/locale'

import type {
  GanttTask,
  GanttMilestone,
  DateRange,
  TaskPosition,
  ZoomLevel,
  TaskDates,
  GanttFilters,
  TaskGroup,
  GroupByOption,
} from './types'
import type { Issue, IssueStatus, MilestoneWithProgress } from '@/hooks'


// ==================== 日期计算 ====================

/**
 * 获取缩放级别对应的每天宽度
 */
export function getColumnWidth(zoomLevel: ZoomLevel): number {
  const config: Record<ZoomLevel, number> = {
    day: 40,
    week: 20,
    month: 8,
  }
  return config[zoomLevel]
}

/**
 * 计算任务在时间轴上的位置
 *
 * @param task 任务数据
 * @param dateRange 可见日期范围
 * @param zoomLevel 缩放级别
 * @returns 任务位置 { left, width }
 */
export function calculateTaskPosition(
  task: { startDate: string; endDate: string },
  dateRange: DateRange,
  zoomLevel: ZoomLevel
): TaskPosition {
  const dayWidth = getColumnWidth(zoomLevel)
  const rangeStart = startOfDay(dateRange.start)

  const taskStart = parseISO(task.startDate)
  const taskEnd = parseISO(task.endDate)

  // 计算相对于范围起点的偏移天数
  const startOffset = differenceInDays(taskStart, rangeStart)
  // 计算任务持续天数（包含结束日期）
  const duration = differenceInDays(taskEnd, taskStart) + 1

  return {
    left: startOffset * dayWidth,
    width: Math.max(duration * dayWidth, dayWidth), // 最小一天宽度
  }
}

/**
 * 计算里程碑在时间轴上的位置
 */
export function calculateMilestonePosition(
  dueDate: string,
  dateRange: DateRange,
  zoomLevel: ZoomLevel
): number {
  const dayWidth = getColumnWidth(zoomLevel)
  const rangeStart = startOfDay(dateRange.start)
  const milestoneDate = parseISO(dueDate)

  return differenceInDays(milestoneDate, rangeStart) * dayWidth + dayWidth / 2
}

/**
 * 判断任务是否逾期
 *
 * @param endDate 结束日期
 * @param status 任务状态
 * @returns 是否逾期
 */
export function isOverdue(endDate: string, status: IssueStatus): boolean {
  // 已完成或已取消的任务不算逾期
  if (status === 'done' || status === 'cancelled') {
    return false
  }

  const today = startOfDay(new Date())
  const taskEndDate = parseISO(endDate)

  return isBefore(taskEndDate, today)
}

/**
 * 计算任务进度
 */
export function calculateProgress(status: IssueStatus): number {
  const progressMap: Record<IssueStatus, number> = {
    backlog: 0,
    todo: 10,
    in_progress: 40,
    code_review: 70,
    testing: 85,
    done: 100,
    blocked: 30,
    cancelled: 0,
  }
  return progressMap[status]
}

/**
 * 根据缩放级别获取日期范围
 */
export function getDateRangeForZoom(
  centerDate: Date,
  zoomLevel: ZoomLevel
): DateRange {
  const center = startOfDay(centerDate)

  switch (zoomLevel) {
    case 'day':
      // 日视图：前后各 15 天
      return {
        start: addDays(center, -15),
        end: addDays(center, 15),
      }
    case 'week':
      // 周视图：前后各 6 周
      return {
        start: startOfWeek(addDays(center, -42), { locale: zhCN }),
        end: endOfWeek(addDays(center, 42), { locale: zhCN }),
      }
    case 'month':
      // 月视图：前后各 3 个月
      return {
        start: startOfMonth(addDays(center, -90)),
        end: endOfMonth(addDays(center, 90)),
      }
  }
}

/**
 * 导航日期范围
 */
export function navigateDateRange(
  currentRange: DateRange,
  direction: 'prev' | 'next',
  zoomLevel: ZoomLevel
): DateRange {
  const delta = direction === 'next' ? 1 : -1

  switch (zoomLevel) {
    case 'day':
      return {
        start: addDays(currentRange.start, delta),
        end: addDays(currentRange.end, delta),
      }
    case 'week':
      return {
        start: addDays(currentRange.start, delta * 7),
        end: addDays(currentRange.end, delta * 7),
      }
    case 'month':
      return {
        start: addDays(currentRange.start, delta * 30),
        end: addDays(currentRange.end, delta * 30),
      }
  }
}

/**
 * 计算时间轴总宽度
 */
export function calculateTimelineWidth(
  dateRange: DateRange,
  zoomLevel: ZoomLevel
): number {
  const days = differenceInDays(dateRange.end, dateRange.start) + 1
  return days * getColumnWidth(zoomLevel)
}

// ==================== 数据转换 ====================

/**
 * 将 Issue 转换为 GanttTask
 *
 * Property 5: Task Bar Position Calculation
 * - 使用数据库 startDate 字段（如果存在）
 * - 添加 isStartDateCalculated 和 isEndDateCalculated 标志
 */
export function issueToGanttTask(
  issue: Issue,
  assigneeName?: string | null,
  milestoneName?: string | null
): GanttTask {
  // 计算开始日期和结束日期，并标记是否为计算值
  const { startDate, endDate, isStartDateCalculated, isEndDateCalculated } =
    calculateTaskDatesWithFlags(issue)

  const status = issue.status
  const progress = calculateProgress(status)
  const taskIsOverdue = isOverdue(endDate, status)

  return {
    id: issue.id,
    code: issue.code,
    title: issue.title,
    type: issue.type,
    startDate,
    endDate,
    status,
    priority: issue.priority,
    assigneeId: issue.assigneeId,
    assigneeName: assigneeName ?? null,
    milestoneId: issue.milestoneId,
    milestoneName: milestoneName ?? null,
    parentId: issue.parentId,
    developerGroup: issue.developerGroup,
    estimatedHours: issue.estimatedHours,
    progress,
    isOverdue: taskIsOverdue,
    isStartDateCalculated,
    isEndDateCalculated,
  }
}

/**
 * 计算任务日期并标记是否为计算值
 *
 * Property 2: Start Date Calculation from Due Date
 * Property 3: Default Date Fallback
 */
function calculateTaskDatesWithFlags(issue: Issue): {
  startDate: string
  endDate: string
  isStartDateCalculated: boolean
  isEndDateCalculated: boolean
} {
  // 优先使用数据库中的 startDate
  const hasDbStartDate = !!issue.startDate
  const hasDbDueDate = !!issue.dueDate

  let startDate: string
  let endDate: string
  let isStartDateCalculated = false
  let isEndDateCalculated = false

  if (hasDbStartDate && hasDbDueDate) {
    // 两个日期都有，直接使用
    startDate = issue.startDate ?? ''
    endDate = issue.dueDate ?? ''
  } else if (hasDbStartDate && !hasDbDueDate) {
    // 只有开始日期，结束日期 = 开始日期 + 1 天
    startDate = issue.startDate ?? ''
    endDate = format(addDays(parseISO(startDate), 1), 'yyyy-MM-dd')
    isEndDateCalculated = true
  } else if (!hasDbStartDate && hasDbDueDate) {
    // 只有截止日期，根据估算工时计算开始日期
    endDate = issue.dueDate ?? ''
    startDate = calculateStartDateFromDueDate(endDate, issue.estimatedHours)
    isStartDateCalculated = true
  } else {
    // 两个日期都没有，使用 createdAt 作为开始日期
    startDate = issue.createdAt.split('T')[0]
    endDate = format(addDays(parseISO(startDate), 1), 'yyyy-MM-dd')
    isStartDateCalculated = true
    isEndDateCalculated = true
  }

  return { startDate, endDate, isStartDateCalculated, isEndDateCalculated }
}

/**
 * 根据截止日期和估算工时计算开始日期
 *
 * Property 2: Start Date Calculation from Due Date
 * - 基于 estimatedHours 计算开始日期
 * - 每天按 8 小时计算
 * - 如果没有估算工时，开始日期 = 截止日期
 *
 * @param dueDate 截止日期 (YYYY-MM-DD)
 * @param estimatedHours 估算工时（小时）
 * @returns 开始日期 (YYYY-MM-DD)
 */
export function calculateStartDateFromDueDate(
  dueDate: string,
  estimatedHours?: number | null
): string {
  const dueDateObj = parseISO(dueDate)

  // 如果有估算工时，按每天 8 小时计算天数
  if (estimatedHours && estimatedHours > 0) {
    const days = Math.ceil(estimatedHours / 8)
    // 开始日期 = 截止日期 - (天数 - 1)，因为截止日期也算一天
    const startDate = addDays(dueDateObj, -(days - 1))
    return format(startDate, 'yyyy-MM-dd')
  }

  // 默认开始日期 = 截止日期
  return dueDate
}

/**
 * 计算任务持续时间（天数）
 *
 * Property 6: Duration Calculation
 * - 返回包含两端日期的天数
 * - duration = endDate - startDate + 1
 *
 * @param startDate 开始日期 (YYYY-MM-DD)
 * @param endDate 结束日期 (YYYY-MM-DD)
 * @returns 持续天数（至少为 1）
 */
export function calculateDuration(startDate: string, endDate: string): number {
  const start = parseISO(startDate)
  const end = parseISO(endDate)

  // 包含两端日期，所以 +1
  const days = differenceInDays(end, start) + 1

  // 至少为 1 天
  return Math.max(days, 1)
}

/**
 * 计算自动适配日期范围
 *
 * Property 4: Auto-Fit Date Range Calculation
 * - 包含所有任务的日期范围
 * - 前后各添加 7 天缓冲区
 * - 如果没有任务，返回以今天为中心的默认范围
 * - 跳过无效日期的任务
 *
 * @param tasks 任务列表
 * @returns 日期范围 { start, end }
 */
export function calculateAutoFitDateRange(tasks: GanttTask[]): DateRange {
  const BUFFER_DAYS = 7
  const today = startOfDay(new Date())

  if (tasks.length === 0) {
    // 没有任务时，返回以今天为中心的默认范围
    return {
      start: addDays(today, -BUFFER_DAYS),
      end: addDays(today, BUFFER_DAYS),
    }
  }

  // 找出所有任务的最早开始日期和最晚结束日期
  let minDate: Date | null = null
  let maxDate: Date | null = null

  for (const task of tasks) {
    const taskStart = parseISO(task.startDate)
    const taskEnd = parseISO(task.endDate)

    // 跳过无效日期的任务
    if (!isValid(taskStart) || !isValid(taskEnd)) {
      continue
    }

    if (!minDate || isBefore(taskStart, minDate)) {
      minDate = taskStart
    }
    if (!maxDate || isAfter(taskEnd, maxDate)) {
      maxDate = taskEnd
    }
  }

  // 如果没有有效日期的任务，返回以今天为中心的默认范围
  if (!minDate || !maxDate) {
    return {
      start: addDays(today, -BUFFER_DAYS),
      end: addDays(today, BUFFER_DAYS),
    }
  }

  // 添加缓冲区
  return {
    start: addDays(minDate, -BUFFER_DAYS),
    end: addDays(maxDate, BUFFER_DAYS),
  }
}

/**
 * 检查是否有任务在当前视图范围外
 *
 * @param tasks 任务列表
 * @param dateRange 当前可见日期范围
 * @returns 是否有任务在视图外
 */
export function hasTasksOutsideView(
  tasks: GanttTask[],
  dateRange: DateRange
): boolean {
  for (const task of tasks) {
    const taskStart = parseISO(task.startDate)
    const taskEnd = parseISO(task.endDate)

    // 跳过无效日期的任务
    if (!isValid(taskStart) || !isValid(taskEnd)) {
      continue
    }

    // 任务完全在视图范围外
    if (isAfter(taskStart, dateRange.end) || isBefore(taskEnd, dateRange.start)) {
      return true
    }
  }

  return false
}

/**
 * 将 Milestone 转换为 GanttMilestone
 */
export function milestoneToGanttMilestone(
  milestone: MilestoneWithProgress
): GanttMilestone | null {
  // 没有截止日期的里程碑不显示
  if (!milestone.dueDate) {
    return null
  }

  const today = startOfDay(new Date())
  const dueDate = parseISO(milestone.dueDate)
  const milestoneIsOverdue = milestone.status === 'open' && isBefore(dueDate, today)

  return {
    id: milestone.id,
    title: milestone.name,
    dueDate: milestone.dueDate,
    status: milestone.status,
    isOverdue: milestoneIsOverdue,
    progress: milestone.progress,
  }
}

// ==================== 拖拽计算 ====================

/**
 * 应用拖拽操作，计算新的日期
 */
export function applyDrag(
  task: { startDate: string; endDate: string },
  deltaX: number,
  edge: 'left' | 'right' | 'body',
  zoomLevel: ZoomLevel
): TaskDates {
  const dayWidth = getColumnWidth(zoomLevel)
  const deltaDays = Math.round(deltaX / dayWidth)

  const startDate = parseISO(task.startDate)
  const endDate = parseISO(task.endDate)

  let newStartDate: Date
  let newEndDate: Date

  switch (edge) {
    case 'body':
      // 整体移动
      newStartDate = addDays(startDate, deltaDays)
      newEndDate = addDays(endDate, deltaDays)
      break
    case 'left':
      // 调整开始日期
      newStartDate = addDays(startDate, deltaDays)
      newEndDate = endDate
      // 确保开始日期不晚于结束日期
      if (isAfter(newStartDate, newEndDate)) {
        newStartDate = newEndDate
      }
      break
    case 'right':
      // 调整结束日期
      newStartDate = startDate
      newEndDate = addDays(endDate, deltaDays)
      // 确保结束日期不早于开始日期
      if (isBefore(newEndDate, newStartDate)) {
        newEndDate = newStartDate
      }
      break
  }

  return {
    startDate: format(newStartDate, 'yyyy-MM-dd'),
    endDate: format(newEndDate, 'yyyy-MM-dd'),
  }
}

/**
 * 验证日期范围是否有效
 */
export function isValidDateRange(dates: TaskDates): boolean {
  const start = parseISO(dates.startDate)
  const end = parseISO(dates.endDate)

  return isValid(start) && isValid(end) && !isAfter(start, end)
}

// ==================== 筛选和分组 ====================

/**
 * 应用筛选条件
 */
export function applyFilters(
  tasks: GanttTask[],
  filters: GanttFilters
): GanttTask[] {
  return tasks.filter((task) => {
    // 负责人筛选
    if (filters.assigneeId && task.assigneeId !== filters.assigneeId) {
      return false
    }

    // 状态筛选
    if (filters.status && filters.status.length > 0) {
      if (!filters.status.includes(task.status)) {
        return false
      }
    }

    // 优先级筛选
    if (filters.priority && filters.priority.length > 0) {
      if (!filters.priority.includes(task.priority)) {
        return false
      }
    }

    // 开发组筛选
    if (filters.developerGroup && task.developerGroup !== filters.developerGroup) {
      return false
    }

    // 里程碑筛选
    if (filters.milestoneId && task.milestoneId !== filters.milestoneId) {
      return false
    }

    // 类型筛选
    if (filters.type && filters.type.length > 0) {
      if (!filters.type.includes(task.type)) {
        return false
      }
    }

    // 工作流阶段筛选
    if (filters.workflowStage && task.currentStage !== filters.workflowStage) {
      return false
    }

    // 超时筛选
    if (filters.isOverdue && !task.isOverdue) {
      return false
    }

    // 回退筛选
    if (filters.isRolledBack && !task.isRolledBack) {
      return false
    }

    return true
  })
}

/**
 * 按指定方式分组任务
 */
export function groupTasks(
  tasks: GanttTask[],
  groupBy: GroupByOption,
  expandedGroups: Set<string>
): TaskGroup[] {
  if (groupBy === 'none') {
    return []
  }

  const groupMap = new Map<string, GanttTask[]>()

  tasks.forEach((task) => {
    let groupId: string
    let groupName: string

    switch (groupBy) {
      case 'milestone':
        groupId = task.milestoneId || 'no-milestone'
        groupName = task.milestoneName || '无里程碑'
        break
      case 'assignee':
        groupId = task.assigneeId || 'unassigned'
        groupName = task.assigneeName || '未分配'
        break
      case 'developerGroup':
        groupId = task.developerGroup || 'no-group'
        groupName = getDeveloperGroupName(task.developerGroup)
        break
      default:
        return
    }

    const key = `${groupId}|${groupName}`
    if (!groupMap.has(key)) {
      groupMap.set(key, [])
    }
    const group = groupMap.get(key)
    if (group) {
      group.push(task)
    }
  })

  return Array.from(groupMap.entries()).map(([key, groupTasks]) => {
    const [id, name] = key.split('|')
    return {
      id,
      name,
      tasks: groupTasks,
      isExpanded: expandedGroups.has(id),
    }
  })
}

/**
 * 获取开发组显示名称
 */
function getDeveloperGroupName(group: string | null): string {
  const names: Record<string, string> = {
    product: '产品',
    design: '设计',
    frontend: '前端',
    backend: '后端',
    testing: '测试',
  }
  return group ? names[group] || group : '未分组'
}

// ==================== 时间轴生成 ====================

/**
 * 生成时间轴刻度
 */
export function generateTimelineMarkers(
  dateRange: DateRange,
  zoomLevel: ZoomLevel
): Array<{ date: Date; label: string; isToday: boolean }> {
  const markers: Array<{ date: Date; label: string; isToday: boolean }> = []
  const today = startOfDay(new Date())
  let current = startOfDay(dateRange.start)

  while (!isAfter(current, dateRange.end)) {
    let label: string

    switch (zoomLevel) {
      case 'day':
        label = format(current, 'MM-dd', { locale: zhCN })
        markers.push({
          date: current,
          label,
          isToday: current.getTime() === today.getTime(),
        })
        current = addDays(current, 1)
        break
      case 'week':
        label = format(current, 'wo 周', { locale: zhCN })
        markers.push({
          date: current,
          label,
          isToday: current.getTime() === today.getTime(),
        })
        current = addDays(current, 7)
        break
      case 'month':
        label = format(current, 'yyyy-MM', { locale: zhCN })
        markers.push({
          date: current,
          label,
          isToday: current.getTime() === today.getTime(),
        })
        current = addDays(current, 30)
        break
    }
  }

  return markers
}

/**
 * 计算今日线位置
 */
export function calculateTodayLinePosition(
  dateRange: DateRange,
  zoomLevel: ZoomLevel
): number | null {
  const today = startOfDay(new Date())

  // 如果今天不在可见范围内，返回 null
  if (isBefore(today, dateRange.start) || isAfter(today, dateRange.end)) {
    return null
  }

  const dayWidth = getColumnWidth(zoomLevel)
  const offset = differenceInDays(today, dateRange.start)

  return offset * dayWidth + dayWidth / 2
}
